import { t, useLanguage } from '../../i18n';
import React, { useState, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { View, StyleSheet, Image, KeyboardAvoidingView, Platform, ScrollView, TouchableOpacity } from 'react-native';
import { TextInput, Button, Text, Snackbar, useTheme, Menu } from 'react-native-paper';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { setDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { Ionicons } from '@expo/vector-icons';

type RegisterScreenProps = {
  navigation: any;
};

const YEAR_OPTIONS = [
  { label: '1年', value: '1' },
  { label: '2年', value: '2' },
  { label: '3年', value: '3' },
  { label: '4年', value: '4' },
  { label: '院1年', value: '5' },
  { label: '院2年', value: '6' },
];

const FACULTY_OPTIONS = [
  { label: '言語文化学部', value: 'lc' },
  { label: '国際社会学部', value: 'ia' },
  { label: '国際日本学部', value: 'js' },
  { label: '大学院総合国際学研究科', value: 'pg' },
];

const YEAR_LABEL_MAP: Record<string, string> = Object.fromEntries(
  YEAR_OPTIONS.map((option) => [option.value, option.label])
);

const FACULTY_LABEL_MAP: Record<string, string> = Object.fromEntries(
  FACULTY_OPTIONS.map((option) => [option.value, option.label])
);

const RegisterScreen = ({ navigation }: RegisterScreenProps) => {
  useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [faculty, setFaculty] = useState('lc');
  const [grade, setGrade] = useState('1');
  const [facultyMenuVisible, setFacultyMenuVisible] = useState(false);
  const [gradeMenuVisible, setGradeMenuVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);
  const [secureTextEntry, setSecureTextEntry] = useState(true);
  const [secureConfirmTextEntry, setSecureConfirmTextEntry] = useState(true);

  const theme = useTheme();

  const handleFacultyChange = (value: string) => {
    setFaculty(value);
    setFacultyMenuVisible(false);
    
    if (value === 'pg') {
      if (grade !== '5' && grade !== '6') {
        setGrade('5');
      }
    } else {
      if (grade !== '1' && grade !== '2' && grade !== '3' && grade !== '4') {
        setGrade('1');
      }
    }
  };

  const handleRegister = async () => {
    if (!name || !email || !password || !confirmPassword || !faculty || !grade) {
      setError(t("すべての項目を入力してください"));
      setVisible(true);
      return;
    }

    if (password !== confirmPassword) {
      setError(t("パスワードが一致しません"));
      setVisible(true);
      return;
    }

    if (password.length < 6) {
      setError(t("パスワードは6文字以上で入力してください"));
      setVisible(true);
      return;
    }

    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update user profile
      await updateProfile(user, {
        displayName: name,
      });

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        name,
        email,
        faculty,
        year: Number(grade),
        createdAt: new Date(),
      });

      // Navigation will be handled by the auth state listener in App.tsx
    } catch (error) {
      console.error(error);
      setError(t("登録に失敗しました。もう一度お試しください"));
      setVisible(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <ScrollView contentContainerStyle={styles.scrollView}>
        <TouchableOpacity style={styles.themeToggle} onPress={toggleTheme}>
          <Ionicons name={isDarkMode ? 'sunny' : 'moon'} size={24} color={theme.colors.primary} />
        </TouchableOpacity>

        <View style={styles.logoContainer}>
          <Image
            source={require('../../assets/app_logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={[styles.title, { color: theme.colors.primary }]}>{t("アカウント登録")}</Text>
        </View>

        <View style={styles.formContainer}>
          <TextInput
            label={t("氏名")}
            value={name}
            onChangeText={setName}
            mode="outlined"
            style={styles.input}
            left={<TextInput.Icon icon={() => <Ionicons name="person" size={20} color={theme.colors.onSurface} />} />}
          />

          <TextInput
            label={t("メールアドレス")}
            value={email}
            onChangeText={setEmail}
            mode="outlined"
            style={styles.input}
            keyboardType="email-address"
            autoCapitalize="none"
            left={<TextInput.Icon icon={() => <Ionicons name="mail" size={20} color={theme.colors.onSurface} />} />}
          />

          <Menu
            visible={facultyMenuVisible}
            onDismiss={() => setFacultyMenuVisible(false)}
            anchor={
              <TouchableOpacity onPress={() => setFacultyMenuVisible(true)}>
                <View pointerEvents="none">
                  <TextInput
                    label={t("学部/研究科")}
                    value={t(FACULTY_LABEL_MAP[faculty] || '')}
                    mode="outlined"
                    style={styles.input}
                    editable={false}
                    left={<TextInput.Icon icon={() => <Ionicons name="school" size={20} color={theme.colors.onSurface} />} />}
                    right={<TextInput.Icon icon={() => <Ionicons name="chevron-down" size={20} color={theme.colors.onSurface} />} />}
                  />
                </View>
              </TouchableOpacity>
            }
          >
            {FACULTY_OPTIONS.map((option) => (
              <Menu.Item 
                key={option.value} 
                onPress={() => handleFacultyChange(option.value)} 
                title={t(option.label)}
              />
            ))}
          </Menu>

          <Menu
            visible={gradeMenuVisible}
            onDismiss={() => setGradeMenuVisible(false)}
            anchor={
              <TouchableOpacity onPress={() => setGradeMenuVisible(true)}>
                <View pointerEvents="none">
                  <TextInput
                    label={t("学年")}
                    value={t(YEAR_LABEL_MAP[grade] || '')}
                    mode="outlined"
                    style={styles.input}
                    editable={false}
                    left={<TextInput.Icon icon={() => <Ionicons name="school" size={20} color={theme.colors.onSurface} />} />}
                    right={<TextInput.Icon icon={() => <Ionicons name="chevron-down" size={20} color={theme.colors.onSurface} />} />}
                  />
                </View>
              </TouchableOpacity>
            }
          >
            {YEAR_OPTIONS
              .filter(option => faculty === 'pg' ? ['5', '6'].includes(option.value) : ['1', '2', '3', '4'].includes(option.value))
              .map(option => (
              <Menu.Item 
                key={option.value} 
                onPress={() => { setGrade(option.value); setGradeMenuVisible(false); }} 
                title={t(option.label)}
              />
            ))}
          </Menu>

          <TextInput
            label={t("パスワード")}
            value={password}
            onChangeText={setPassword}
            mode="outlined"
            style={styles.input}
            secureTextEntry={secureTextEntry}
            right={
              <TextInput.Icon
                icon={() => <Ionicons name={secureTextEntry ? "eye" : "eye-off"} size={20} color={theme.colors.onSurface} />}
                onPress={() => setSecureTextEntry(!secureTextEntry)}
              />
            }
            left={<TextInput.Icon icon={() => <Ionicons name="lock-closed" size={20} color={theme.colors.onSurface} />} />}
          />

          <TextInput
            label={t("パスワード（確認）")}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            mode="outlined"
            style={styles.input}
            secureTextEntry={secureConfirmTextEntry}
            right={
              <TextInput.Icon
                icon={() => <Ionicons name={secureConfirmTextEntry ? "eye" : "eye-off"} size={20} color={theme.colors.onSurface} />}
                onPress={() => setSecureConfirmTextEntry(!secureConfirmTextEntry)}
              />
            }
            left={<TextInput.Icon icon={() => <Ionicons name="lock-closed" size={20} color={theme.colors.onSurface} />} />}
          />

          <Button
            mode="contained"
            onPress={handleRegister}
            style={styles.button}
            loading={loading}
            disabled={loading}
          >
            {t("登録")}
          </Button>

          <View style={styles.loginContainer}>
            <Text style={{ color: theme.colors.onSurface }}>{t("すでにアカウントをお持ちの場合は")} </Text>
            <TouchableOpacity onPress={() => navigation.navigate('Login')}>
              <Text style={[styles.link, { color: theme.colors.primary }]}>{t("ログイン")}</Text>
            </TouchableOpacity>
          </View>
        </View>

        <Snackbar
          visible={visible}
          onDismiss={() => setVisible(false)}
          duration={3000}
          action={{
            label: t("閉じる"),
            onPress: () => setVisible(false),
          }}
        >
          {error}
        </Snackbar>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
  },
  themeToggle: {
    position: 'absolute',
    top: 40,
    right: 20,
    padding: 10,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 30,
  },
  logo: {
    width: 80,
    height: 80,
    marginBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  input: {
    marginBottom: 12,
  },
  button: {
    marginTop: 8,
    marginBottom: 16,
    paddingVertical: 8,
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 16,
  },
  link: {
    fontWeight: 'bold',
  },
});

export default RegisterScreen;
